import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import axios from "axios";
import { Icons } from "../../pages/doctor-console/Utils/iconsRegistry";
import { toast } from "react-toastify";

const { REACT_APP_BACKEND_ROUTE_URL } = process.env;

const PasswordChangeModal = ({ open, onClose, enforceChange = false }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (error) setError("");
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const headers = {
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };
  const validateForm = () => {
    const { currentPassword, newPassword, confirmPassword } = formData;

    const errors = {};

    if (!currentPassword) {
      errors.currentPassword = "Current password is required.";
    }

    if (!newPassword) {
      errors.newPassword = "New password is required.";
    } else if (
      !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/.test(newPassword)
    ) {
      errors.newPassword =
        "Password must include an alphabet, a number, a special character, and be at least 8 characters";
    } else if (newPassword === currentPassword) {
      errors.newPassword =
        "New password must be different from current password";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required.";
    } else if (
      !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/.test(
        confirmPassword
      )
    ) {
      errors.confirmPassword =
        "Password must include an alphabet, a number, a special character, and be at least 8 characters";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "New password and confirm password do not match";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    const apiUrl = `${REACT_APP_BACKEND_ROUTE_URL}change_password/`;
    const requestBody = {
      current_password: formData.currentPassword,
      new_password: formData.newPassword,
      confirm_password: formData.confirmPassword,
    };

    axios
      .post(apiUrl, requestBody, {
        headers: headers,
      })
      .then((response) => {
        const data = response?.data;
        const statusValue =
          (data &&
            (data.status ?? data.Status ?? data.success ?? data.Success)) ??
          null;
        const isValidFlag =
          typeof data?.is_valid === "boolean" ? data.is_valid : null;

        const isFailureFromStatus =
          typeof statusValue === "string"
            ? statusValue.toLowerCase() !== "success"
            : typeof statusValue === "boolean"
            ? statusValue === false
            : false;
        const isFailureFromValid = isValidFlag === false;
        const isFailure = isFailureFromStatus || isFailureFromValid;

        if (isFailure) {
          const message =
            data?.message ||
            data?.error ||
            "Failed to change password. Please try again.";
          toast.error(message);
          return;
        }

        const successMessage =
          data?.message || "Password changed successfully!";
        toast.success(successMessage);
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      })
      .catch((err) => {
        console.error("Password change error:", err);
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to change password. Please try again.";
        setError("");
        toast.error(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    if (!loading && !enforceChange) {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setError("");
      setSuccess("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (enforceChange) return; // prevent closing by backdrop/escape when enforced
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={enforceChange}
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          // backgroundImage: `url(${Icons.modal_blue})`,
          backgroundColor: "#213360",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: "20px", color: "#FFFFFF" }}
        >
          Change Password
        </Typography>
        <IconButton onClick={handleClose} disabled={loading || enforceChange}>
          <CloseIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {enforceChange && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              For security, please change your password before continuing.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success notifications are now shown via window.alert */}

          <div className="form-group">
            <label>
              Current Password <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPasswords.current ? "text" : "password"}
                className={`form-control ${
                  fieldErrors.currentPassword ? "is-invalid" : ""
                }`}
                value={formData.currentPassword}
                onChange={handleInputChange("currentPassword")}
                disabled={loading}
                placeholder="Enter current password"
                style={{ paddingRight: "50px" }}
              />
              <IconButton
                onClick={() => handleTogglePasswordVisibility("current")}
                disabled={loading}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  padding: "4px",
                }}
              >
                {showPasswords.current ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>
            {fieldErrors.currentPassword && (
              <div
                style={{
                  color: "red",
                  fontSize: "12px",
                  marginTop: "5px",
                  textAlign: "left",
                }}
              >
                {fieldErrors.currentPassword}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              New Password <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPasswords.new ? "text" : "password"}
                className={`form-control ${
                  fieldErrors.newPassword ? "is-invalid" : ""
                }`}
                value={formData.newPassword}
                onChange={handleInputChange("newPassword")}
                disabled={loading}
                placeholder="Enter new password"
                style={{ paddingRight: "50px" }}
              />
              <IconButton
                onClick={() => handleTogglePasswordVisibility("new")}
                disabled={loading}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  padding: "4px",
                }}
              >
                {showPasswords.new ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>
            {fieldErrors.newPassword && (
              <div
                style={{
                  color: "red",
                  fontSize: "12px",
                  marginTop: "5px",
                  textAlign: "left",
                }}
              >
                {fieldErrors.newPassword}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              Confirm New Password <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPasswords.confirm ? "text" : "password"}
                className={`form-control ${
                  fieldErrors.confirmPassword ? "is-invalid" : ""
                }`}
                value={formData.confirmPassword}
                onChange={handleInputChange("confirmPassword")}
                disabled={loading}
                placeholder="Confirm new password"
                style={{ paddingRight: "50px" }}
              />
              <IconButton
                onClick={() => handleTogglePasswordVisibility("confirm")}
                disabled={loading}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  padding: "4px",
                }}
              >
                {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>
            {fieldErrors.confirmPassword && (
              <div
                style={{
                  color: "red",
                  fontSize: "12px",
                  marginTop: "5px",
                  textAlign: "left",
                }}
              >
                {fieldErrors.confirmPassword}
              </div>
            )}
          </div>
        </Box>
      </DialogContent>

      {/* <DialogActions sx={{ p: 3, pt: 1 }}>
        <button
          onClick={handleClose}
          disabled={loading || enforceChange}
          className="btn btn__secondary btn__rounded"
          style={{ marginRight: "10px" }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn btn__secondary btn__rounded btn__block btn__xhight"
          style={{ minWidth: "150px" }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Change Password"
          )}
        </button>
      </DialogActions> */}

      <DialogActions sx={{ p: 3, pt: 1, display: "flex", gap: 2 }}>
        <button
          onClick={handleClose}
          disabled={loading || enforceChange}
          className="btn btn__secondary btn__rounded"
          style={{ flex: 1 }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn btn__secondary btn__rounded btn__block btn__xhight"
          style={{ flex: 1 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Change Password"
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordChangeModal;
